import { Alert, Box, CircularProgress, Icon, Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import Navigation, { NavigationTag } from "./Navigation";
import activities from "../../mock/data.json";
import ActivityCard from "./ActivityCard";
import NextActivity from "./NextActivity";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentUser } from "lib/cognito";
import assessmentService, {
  GetModulesResponse,
  ModuleStatuses,
} from "services/assessmentService";
import { ReactComponent as VectorLogo } from "../../static/svgs/vector.svg";
import { ReactComponent as BackIcon } from "../../static/svgs/BackArrowIcon.svg";
import { ReactComponent as CollVector } from "../../static/svgs/CollVector.svg";
import { ReactComponent as CommVector } from "../../static/svgs/CommVector.svg";
import { ReactComponent as CritVector } from "../../static/svgs/CritVector.svg";
import schoolJoyImage from "../../static/svgs/schoolJoy.svg";
import { setAssociatedSkillsRedux, AssociatedSkillsCredit, AssociatedSkillsArea, AssociatedSkillsData, AssociatedSkillsState } from "redux/slices/associatedSkills/associatedSkillsSlice";

import CollapsibleCard from "./CollapsibleCard";
import ArtifactPage from "./ArtifactPage";
import SkillsPage from "./SkillsPage";
import educatorService from "services/educatorService";
import CarouselButtons from "./CarouselButtons";
import NewSkillsPage from "./NewSkillsPage";
import BrowserCheckMessagePanel from "components/BrowserCheckMessagePanel";

// ...existing code...

const BasePage = () => {
  const location = useLocation();
  const divARef = useRef<HTMLDivElement | null>(null);
  const cards = ["Coll", "Comm", "Crit"];
  let { user, sub, name } = location.state || {};
  const user1 = useSelector((state: any) => state.account);
  const isPlanningLibrary = useSelector((state:any) => state?.account?.isPlanningLibrary ?? false);
  const { id, role, schoolId } = useSelector((state: any) => state.account) || "";
  const [refresh, setRefresh] = useState(false);
  user = sessionStorage.getItem("userId") || user;
  sub = sessionStorage.getItem("userSub") || sub;
  name = sessionStorage.getItem("userName") || name;
  const skillCount: Record<string, number> = {};
  const [count, setCount] = useState<any>({});
  const [arti, setArti] = useState<any>([]);
  const [artiCounts, setArtiCounts] = useState<any>({});
  let userId = user ? user : id;
  const fileUrl = `${process.env.PUBLIC_URL}/pdf_file.pdf`;
  const navigate = useNavigate();
  const intersectionElementRef = useRef<Element | Document | null>(null);

  const [loadingStatus, setLoadingStatus] =
    useState<"idle" | "pending" | "error" | "success">("pending");
  const [assessments, setAssessment] = useState<GetModulesResponse["data"]>([]);
  const [isFixed, setIsFixed] = useState(true);
  const [associatedSkills, setAssociatedSkills] = useState<any | null>(null);
  const [associatedSkillsLibrary, setAssociatedSkillsLibrary] = useState<any | null>(null);
  const skillsRedux = useSelector((state: any) => state.associatedSkills?.data);

  const hasFetchedSkills = useRef<boolean>(false);
  const dispatch = useDispatch();

  const schoolJoy = [
    {
      id: "CPS",
      name: "Skills Chat",
      shortDescription: "",
      launchURL: process.env.REACT_APP_SCHOOL_JOY_LAUNCH_URL,
      insightsURL: process.env.REACT_APP_SCHOOL_JOY_LAUNCH_URL,
      duration: 300,
      requiresVideo: 0,
      requiresAudio: 0,
      sequence: 1,
      type: "schooljoy",
      imageUrl: schoolJoyImage,
      skills: [
        { skillId: "Tell us about your outside of school activities.", activities: [], subSkillIds: [] },
      ],
      activities: [
      ],
    },
  ];

  const baseTextStyle = {
    color: 'rgba(0, 0, 0, 1)',
    fontSize: '14px',
    fontWeight: '400',
    display: 'flex',
    flexDirection: 'row',
    gap: '2px',
    letterSpacing: '1.68px'
  };

  const boldTextStyle = {
    fontWeight: '700',
    color: 'rgba(0, 0, 0, 1)',
    fontSize: '14px',
    letterSpacing: '1.68px'
  };

  const getCountValue = (card: any) => {
    if (card === "Comm") return count["communication"] ?? 0;
    if (card === "Coll") return count["collaboration"] ?? 0;
    if (count["criticalthinking"] != null && count["critical thinking"] != null) {
      return count["criticalthinking"] + count["critical thinking"];
    }
    else {
      return count["critical thinking"] ?? count["criticalthinking"] ?? 0
    }
  };

  const getArtifactValue = (card: any) => {
    if (card === "Comm") return artiCounts["communication"] ?? 0;
    if (card === "Coll") return artiCounts["collaboration"] ?? 0;
    return artiCounts["critical thinking"] ?? 0;
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setRefresh(prevRefresh => !prevRefresh);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (hasFetchedSkills.current == true) {
      fetchAssessments()
    }
  },
    [refresh])

  const fetchAssessments = useCallback(async () => {
    setLoadingStatus("pending");
    try {
      const promises = [
        assessmentService.getApprizeModules(userId, sub),
      ];

      // Insert edge call at the beginning if not planning library
      if (!isPlanningLibrary) {
        promises.splice(0, 0, assessmentService.getEdgeModules(userId, sub));
      }

      // Await all promises
      const results = await Promise.all(promises);
      let edgeAssessments: any[] = [];
      let apprizeAssessments: any[] = [];

      if (!isPlanningLibrary) {
        [edgeAssessments, apprizeAssessments] = results;
      } else {
        [apprizeAssessments] = results;
        edgeAssessments = [];
      }

      // Ensure type is set on each module
      edgeAssessments = (edgeAssessments || []).map((m: any) => ({ ...m, type: m.type || "edge" }));
      apprizeAssessments = (apprizeAssessments || []).map((m: any) => ({ ...m, type: m.type || "apprize" }));

      setAssessment([
        ...schoolJoy,
        ...edgeAssessments,
        ...apprizeAssessments,
      ]);

      setLoadingStatus("success");
    } catch (e) {
      console.error("An error occurred while fetching assessments:", e);
      setLoadingStatus("error");
    }
  }, [userId, sub, schoolId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsFixed(true);
            if (role !== "ROLE_EDUCATOR")
              navigate(`/base/#${entry.target.id}`);
          }
          if (entry.target.id === "activities" && !hasFetchedSkills.current) {
            setIsFixed(!entry.isIntersecting);
            hasFetchedSkills.current = true;
            fetchAssessments();
          }
        });
      },
      { rootMargin: "0px 0px -75%", root: intersectionElementRef.current }
    );

    Array.from(document.querySelectorAll("div[id]"))
      .filter((section) => section.id !== "root")
      .forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [navigate]);

  useEffect(() => {
    sessionStorage.setItem("schoolId", schoolId);
    fetchAssociatedSkills(schoolId);
    // isPlanningLibrary && fetchAssociatedSkillsLibrary(schoolId);
  }, [userId, schoolId, isPlanningLibrary]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      console.log(el)
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [role == 'ROLE_EDUCATOR']);

  const fetchAssociatedSkills =
    async (schoolId: string): Promise<AssociatedSkillsData | null> => {
      try {
        if (skillsRedux?.id && Array.isArray(skillsRedux.areas) && skillsRedux.areas.length > 0) {
          setAssociatedSkills(skillsRedux);
          return skillsRedux as AssociatedSkillsData;
        }
        
        const [associatedSkillsResponse, competencyResponse] = await Promise.all([
          educatorService.fetchAssociatedSkills(schoolId,isPlanningLibrary),
          educatorService.getCompetencyModel(schoolId),
        ]);

        if (associatedSkillsResponse && competencyResponse) {
          const associatedSkillsState: AssociatedSkillsState = {
            ...associatedSkillsResponse,
            visibleLevels: competencyResponse.visible_levels || 3,
          };
          console.log("SCHOOL DATA LOADED ", associatedSkillsState);

          setAssociatedSkills(associatedSkillsState as AssociatedSkillsState);

          const requiredProperties = ["id", "schoolId", "areas", "visibleLevels"] as const;
          if (requiredProperties.every(prop => prop in associatedSkillsState)) {
            dispatch(setAssociatedSkillsRedux({
              id: (associatedSkillsState as any).id,
              schoolId: (associatedSkillsState as any).schoolId,
              areas: (associatedSkillsState as any).areas,
              visibleLevels: (associatedSkillsState as any).visibleLevels,
              ...(( associatedSkillsResponse as any).schoolModel ? { schoolModel: (associatedSkillsResponse as any).schoolModel } : {})
            }));
          }

          return associatedSkillsResponse; // Return the typed data for further use
        }

        return null;
      } catch (error) {
        console.error("Error fetching associated skills:", error);
        return null;
      }
    };
  // const fetchAssociatedSkillsLibrary =
  //   async (schoolId: string): Promise<AssociatedSkillsState | null> => {
  //     try {
  //       const data: AssociatedSkillsState | null =
  //         await educatorService.fetchAssociatedSkills(schoolId, true);
  //       if (data) {
  //         setAssociatedSkillsLibrary(data);
  //         return data; // Return the typed data for further use
  //       }

  //       return null;
  //     } catch (error) {
  //       console.error("Error fetching associated skills:", error);
  //       return null;
  //     }
  //   };

  const loadingStatusUI: Record<typeof loadingStatus, ReactNode> = {
    error: (
      <Alert severity="error">
        An error occurred while fetching assessments.
      </Alert>
    ),
    idle: null,
    pending: <CircularProgress />,
    success: null,
  };

  useEffect(() => {
    const data = assessments.filter(item => item?.status?.status == "COMPLETED")
    data.forEach(item => {
      const skill = item.skills.map(item => item.skillId.toLowerCase());
      skill.map((id => {
        skillCount[id] = (skillCount[id] || 0) + 1;
      }))
    });
    setCount(skillCount);
  }, [assessments])

  useEffect(() => {
    async function fetchData() {
      let artifactData = await educatorService.getArtifacts(
        user ? user : user1.id,
        1,
        100,
      );
      if (artifactData) {
        setArti(artifactData);
      }
    }
    fetchData();
  }, [])


  useEffect(() => {
    if (arti) {
      if (arti.items) {
        let areas = arti.items?.filter((item: any) => item.areas.length > 0)?.map((x: any) => x.areas)

        let title = areas?.map((area: any) => area.map((title: any) => title.title.toLowerCase()))
        let flatTitle = title?.flat();

        let artiCount = flatTitle.reduce((acc: any, cur: any) => {
          acc[cur] = (acc[cur] || 0) + 1
          return acc;
        }, {})
        setArtiCounts(artiCount);
      }
    }
  }, [arti])


  const isActivityLocked = (index: number) => {
    if (role === "ROLE_EDUCATOR") {
      return true;
    }
    if (index == 0) {
      return false;
    }
    if (index > 0) {
      if (assessments[index - 1].status) {
        if (assessments[index - 1].status.status == "COMPLETED") {
          return false;
        }
        if (assessments[index - 1].status.status == "IN_PROGRESS") {
          return true;
        }
      } else {
        return true;
      }
    }
  };

  const navigateToEducator = () => {
    sessionStorage.removeItem("userId");
    sessionStorage.removeItem("userSub");
    sessionStorage.removeItem("userName");
    navigate("/educator");
  };


  return (
    <Box
      flexWrap="nowrap"
      overflow="hidden"
      height="100%"
      sx={{ backgroundColor: "rgba(254, 253, 251, 1)" }}
    >
      {/* <Box sx={{ height: "100%", width: "fit-content", flex: 1 }}>
        <Navigation handleNavigate={handleNavigate} />
      </Box> */}
      <Box flex={1} height="100%" display="flex" flexDirection="column">
        {loadingStatus === "pending" && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {role === "ROLE_EDUCATOR" && (
          <Box
            sx={{
              zIndex: 2,
              backgroundColor: "rgba(210, 244, 255, 1)",
              position: "relative",
              top: 0,
              width: "100%",
              height: "58px",
              alignItems: "center",
              justifyItems: "center",
              pl: "24px",
            }}
            display="flex"
            flexDirection="row"
          >
            <BackIcon
              onClick={navigateToEducator}
              style={{ cursor: "pointer" }}
            />
            <Typography
              sx={{
                fontFamily: "Open Sans",
                fontSize: "13px",
                fontWeight: 400,
                pl: "10px",
                cursor: "pointer"
              }}
              onClick={navigateToEducator}
            >
              Back to All Learners
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography
              sx={{
                fontFamily: "Open Sans",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {`YOU'RE VIEWING ${name}`}
            </Typography>
            <Box sx={{ flexGrow: 1.5 }} />
          </Box>
        )}
        <Header />
        <BrowserCheckMessagePanel />
        <Box
          ref={intersectionElementRef}
          sx={{
            overflow: "auto",
            height: "100%",
            position: "relative",
            "&::-webkit-scrollbar": {
              display: "none", // For Chrome, Safari, and Edge
            },
            "-ms-overflow-style": "none", // For Internet Explorer and Edge
            "scrollbar-width": "none",
          }}
        >
          {/* Home Section for learner */}
          {<Box px={5} pt={5} id="home" sx={[window.innerWidth == 1024 ? { minHeight: "30vh" } : { minHeight: '48vh' }]}>
            <Typography sx={{ fontSize: '24px', fontWeight: 500, marginLeft: '20px' }}> Welcome, {user1?.firstName.split(' ')[0]}!</Typography>
            <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'row', gap: '30px', padding: '20px', paddingRight: '40px', alignContent: 'center', justifyContent: 'center' }}>

              {cards.map(card => (<Paper sx={[{ width: 302, height: 178, borderRadius: '8px', overflow: 'hidden' }, card == "Coll" ? { border: '1px solid rgba(255, 166, 126, 1)' } : card == "Comm" ? { border: '1px solid rgba(252, 207, 234, 1)' } : { border: '1px solid rgba(68, 210, 189, 1)' }]}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', }}>

                  {/* Upper Half: Icon and Text */}
                  <Box sx={[{ height: '50%', display: 'flex', justifyContent: 'left', paddingTop: 10 }, card == "Coll" ? { backgroundColor: 'rgba(255, 243, 238, 1)' } : card == "Comm" ? { backgroundColor: 'rgba(255, 250, 253, 1)' } : { backgroundColor: 'rgba(230, 249, 246, 1)' }]}>
                    {card == "Coll" ? <Icon sx={{ height: '32px', width: '32px', marginLeft: '32px' }}> <CommVector /> </Icon> :
                      card == "Comm" ? <Icon sx={{ height: '32px', width: '32px', marginLeft: '32px' }}> <CollVector /> </Icon> :
                        <Icon sx={{ height: '32px', width: '32px', marginLeft: '32px' }}> <CritVector /> </Icon>}


                    <Typography sx={{ color: 'rgba(0, 0, 0, 1)', fontSize: '12px', fontWeight: 400, letterSpacing: '1.44px', textTransform: 'uppercase', marginTop: '5px', marginLeft: '16px' }}>
                      {card == "Coll" ? "Collaboration" : card == "Comm" ? "Communication" : "Critical Thinking"}
                    </Typography>
                  </Box>

                  {/* Lower Half: Number with Vertical Lines and Text */}
                  <Box sx={[{ height: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }, card == "Coll" ? { backgroundColor: 'rgba(255, 166, 126, 1)' } : card == "Comm" ? { backgroundColor: 'rgba(252, 207, 234, 1)' } : { backgroundColor: 'rgba(68, 210, 189, 1)' }]}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      {/* First Column: Number */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '16px' }}>
                        <Typography sx={{ color: 'rgba(0, 0, 0, 1)', fontSize: '40px', fontWeight: '600' }}>
                          {getCountValue(card) + getArtifactValue(card)}
                        </Typography>
                      </Box>

                      {/* Second Column: Vertical Line and Text */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                        <Typography sx={baseTextStyle}>
                          <Typography sx={boldTextStyle}>{getCountValue(card)}</Typography>ACTIVITIES
                        </Typography>

                        <Typography sx={baseTextStyle}>
                          <Typography sx={boldTextStyle}>{getArtifactValue(card)}</Typography>ARTIFACTS
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                </Box>
              </Paper>))}
            </Paper>
          </Box>}


        
          {/* Activities section */}
          <Box
            px={5}
            pt={5}
            id="activities"
            sx={{ minHeight: "100vh", zIndex: 1 }}
          >
            {loadingStatusUI[loadingStatus] == "success" || (
              <>
                <VectorLogo
                  ref={divARef}
                  style={{
                    fill: "rgba(20, 96, 214, 1)",
                    position: "absolute",
                    right: 0,
                    top: window.innerWidth == 1024 ? 285 : window.innerHeight / 2,
                    width: "100%",
                    backgroundColor: "rgba(0, 82, 210, 1)",
                    height: "140px",
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    alignContent: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    verticalAlign: 'center',
                    marginTop: '2%',
                    color: 'rgba(255, 255, 255, 1)',
                    fontWeight: 600,
                    fontSize: '40px',
                  }}
                >
                  My Activities
                </Box>
                {/* <Typography>Continue on your Next Activity</Typography> */}
                <Box sx={{ mt: "60px", display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px' }}>
                  {assessments.length != 0 &&
                    assessments.map((assessment: any, index: number) => (
                      <ActivityCard module={assessment} isNextActivity={isActivityLocked(index)} index={index} />
                    ))}
                </Box>
              </>
            )}

          </Box>
          {/* Artifacts section */}
          <Box id="artifacts" mt={5} px={5}>
            <Box sx={{ backgroundColor: "white", minHeight: "100vh" }}>
              <ArtifactPage
                id={user ? user : ""}
                skillsData={associatedSkills?.schoolModel || associatedSkills}
              />
            </Box>
          </Box>
          {/* Evidences section */}
          <Box id="skills" mt={5} px={5}>
            <Box sx={{ backgroundColor: "white", minHeight: "100vh" }}>
              <NewSkillsPage skillsData={associatedSkills} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BasePage;
